import { aircraftType } from '../data/aircraft';
import type { AircraftAsset, AircraftType, GameEvent, GameState } from '../game/types';

export type CrewEmotion = 'angry' | 'grievance' | 'normal' | 'happy' | 'excited';

export function crewEmotion(mood: number): CrewEmotion {
  if (mood <= 20) return 'angry';
  if (mood <= 40) return 'grievance';
  if (mood <= 60) return 'normal';
  if (mood <= 80) return 'happy';
  return 'excited';
}

export function crewEmotionLabel(mood: number): string {
  const map: Record<CrewEmotion, string> = {
    angry: '愤怒',
    grievance: '委屈',
    normal: '正常',
    happy: '愉快',
    excited: '亢奋',
  };
  return map[crewEmotion(mood)];
}

export function crewWeeklyBaseSalary(type: AircraftType): number {
  // 把固定成本中约 25% 视为机组人员基准周薪
  return Math.round(type.weeklyFixedCost * 0.25);
}

export function crewSeverance(aircraft: AircraftAsset, currentWeek: number): number {
  const type = aircraftType(aircraft.typeId);
  const base = crewWeeklyBaseSalary(type);
  const years = Math.max(0, Math.floor((currentWeek - aircraft.crewHiredWeek) / 52));
  return base * (years + 1);
}

export function weeklyCrewMoodDelta(salaryModifier: number): number {
  // 80% 工资为情绪平衡点，情绪稳定在默认值 60；高于 80% 回升，低于 80% 下降
  if (salaryModifier >= 0.8) {
    return (salaryModifier - 0.8) * 10;
  }
  return -(0.8 - salaryModifier) * 15;
}

export function updateAircraftCrewMood(aircraft: AircraftAsset): void {
  aircraft.crewMood += weeklyCrewMoodDelta(aircraft.crewSalaryModifier);
  aircraft.crewMood = Math.max(0, Math.min(100, aircraft.crewMood));
}

export function isCrewAngry(aircraft: AircraftAsset): boolean {
  return crewEmotion(aircraft.crewMood) === 'angry';
}

export function createCrewResignationEvent(state: GameState, aircraft: AircraftAsset, cost: number): GameEvent {
  const type = aircraftType(aircraft.typeId);
  return {
    id: `event-${state.week}-crew-${aircraft.id}`,
    week: state.week,
    tone: 'negative',
    title: `${type.name} 机组${aircraft.lease ? '罢工' : '离职'}`,
    detail: aircraft.lease
      ? `租赁的 ${type.name} 机组情绪愤怒，本周触发赔偿并停飞。请及时调整薪资水平。`
      : `自有的 ${type.name} 机组情绪愤怒并选择离职，公司已支付 N+1 赔偿。新机组已就位。`,
    impact: {
      aircraftId: aircraft.id,
      aircraftName: type.name,
      cost,
      lostRevenue: 0,
    },
  };
}

export function averageCrewMood(aircraftIds: string[], state: GameState): number {
  const assets = aircraftIds
    .map((id) => state.aircraft.find((a) => a.id === id))
    .filter((a): a is AircraftAsset => a !== undefined);
  if (!assets.length) return 50;
  return assets.reduce((sum, a) => sum + a.crewMood, 0) / assets.length;
}

export function routeReputationDelta(averageMood: number): number {
  if (averageMood >= 80) return 1;
  if (averageMood >= 60) return 0.2;
  // 服务差时下降更快，100→60 约 8 周
  return -5;
}

export function routeReputationMultiplier(score: number): number {
  return Math.max(0, score / 25);
}
